/**
 * Assemble a molecular formula from atom symbols using Hill system ordering.
 *
 * Hill system rules:
 * - If carbon is present: C first, H second, then remaining elements alphabetically
 * - If no carbon: all elements alphabetically
 * - Subscript counts greater than 1 (e.g., Cl × 3 → Cl₃)
 * - Single atoms get no subscript (e.g., Al × 1 → Al)
 *
 * Exception: Hydrogen halides (HF, HCl, HBr, HI) always put H first.
 *
 * @example
 * assembleMolecularFormula(['Al', 'Cl', 'Cl', 'Cl']) // → "AlCl₃"
 * assembleMolecularFormula(['B', 'F', 'F', 'F'])     // → "BF₃"
 * assembleMolecularFormula(['C', 'H', 'H', 'Cl', 'Cl']) // → "CH₂Cl₂"
 * assembleMolecularFormula(['O', 'H', 'H'])          // → "H₂O"
 * assembleMolecularFormula(['H', 'Cl'])              // → "HCl" (not "ClH")
 *
 * @see PIPH-109
 */

/**
 * Halogens that form hydrogen halides with special ordering (H first)
 */
const HALOGENS = new Set(['F', 'Cl', 'Br', 'I']);

/**
 * Check if the atom counts represent a hydrogen halide (HF, HCl, HBr, HI).
 * These get special treatment: H always comes first regardless of Hill system.
 */
function isHydrogenHalide(counts: Map<string, number>): boolean {
    if (counts.size !== 2) return false;
    const hasOneH = counts.get('H') === 1;
    const hasOneHalogen = Array.from(counts.keys()).some(
        sym => HALOGENS.has(sym) && counts.get(sym) === 1
    );
    return hasOneH && hasOneHalogen;
}

/**
 * Convert a number to Unicode subscript characters
 */
function toSubscript(n: number): string {
    const subscripts: Record<string, string> = {
        '0': '₀',
        '1': '₁',
        '2': '₂',
        '3': '₃',
        '4': '₄',
        '5': '₅',
        '6': '₆',
        '7': '₇',
        '8': '₈',
        '9': '₉',
    };
    return String(n)
        .split('')
        .map(digit => subscripts[digit] ?? digit)
        .join('');
}

/**
 * Assemble a molecular formula from a list of atom symbols.
 *
 * @param atomSymbols Array of element symbols (e.g., ['Al', 'Cl', 'Cl', 'Cl'])
 * @returns Molecular formula string with subscripts (e.g., "AlCl₃")
 */
export function assembleMolecularFormula(atomSymbols: string[]): string {
    if (atomSymbols.length === 0) {
        return '';
    }

    // Count atoms by symbol
    const counts = new Map<string, number>();
    for (const sym of atomSymbols) {
        counts.set(sym, (counts.get(sym) || 0) + 1);
    }

    // Hill system sort with hydrogen halide exception
    const elements = Array.from(counts.keys());
    const hasCarbon = counts.has('C');
    const hydrogenHalide = isHydrogenHalide(counts);

    elements.sort((a, b) => {
        // Hydrogen halide exception: H always first (HCl, HF, HBr, HI)
        if (hydrogenHalide) {
            if (a === 'H') return -1;
            if (b === 'H') return 1;
        }
        if (hasCarbon) {
            // Carbon first, then hydrogen, then alphabetical
            if (a === 'C') return -1;
            if (b === 'C') return 1;
            if (a === 'H') return -1;
            if (b === 'H') return 1;
        }
        // Alphabetical for everything else (or all if no carbon)
        return a.localeCompare(b);
    });

    // Build formula with subscripts
    return elements
        .map(el => {
            const count = counts.get(el)!;
            return count === 1 ? el : `${el}${toSubscript(count)}`;
        })
        .join('');
}
